/**
 * Clarity One Statement of Account — shared types and pure money rules.
 * Authoritative totals are assembled server-side; the client only renders.
 *
 * Commercial ledger rules:
 * - Outstanding = remaining accepted contract value after payments/credits
 * - Due = remaining posted/issued invoice balance
 * - Proposal approval establishes contract Outstanding; invoices move value into Due
 * - Payments reduce Outstanding (when proposal-linked) and Due
 */
import { roundSgd2 } from "./cardPaymentFee";
import {
  addSingaporeCalendarDays,
  getSingaporeDateKeyFromInstant,
  singaporeTodayDateKey,
} from "./timezone";

export const CLARITY_ONE_STATEMENT_CURRENCY = "SGD" as const;

export type ClarityOneStatementInvoiceStatus =
  | "unpaid"
  | "partially_paid"
  | "paid"
  | "overdue"
  | "void";

export type ClarityOneStatementTxnKind =
  | "acceptance"
  | "invoice"
  | "payment"
  | "credit_note"
  | "refund";

export type ClarityOneStatementActivityBadge =
  | "APPROVED"
  | "PAYMENT"
  | "INVOICE"
  | "CREDIT"
  | "CANCELLED"
  | "VOIDED";

export type ClarityOneStatementAction =
  | { type: "view_proposal"; proposalId: number }
  | { type: "download_accepted_pdf"; proposalId: number }
  | { type: "view_invoice"; kind: "portal" | "deal"; token: string }
  | { type: "view_receipt"; kind: "portal" | "deal"; token: string }
  | { type: "view_vo"; token: string }
  | { type: "none" };

export type ClarityOneStatementDateRange = "all" | "last_30_days" | "last_90_days" | "this_year";

export type ClarityOneStatementTransaction = {
  id: string;
  kind: ClarityOneStatementTxnKind;
  /** ISO date (YYYY-MM-DD) for display sorting. */
  date: string;
  /** Full ISO timestamp for precise ordering. */
  sortAt: string;
  reference: string;
  /** Legacy detail string (internal assembly); prefer activityLabel for display. */
  details: string;
  /** Customer-facing activity title. */
  activityLabel: string;
  activityBadge: ClarityOneStatementActivityBadge;
  /** Project title for the unified ledger Project column. */
  projectTitle: string | null;
  proposalId: number | null;
  /**
   * Contract amount (approval) or invoice amount (invoice row).
   * Payments leave this at 0.
   */
  billed: number;
  /** Payment / credit / refund amount. */
  paidCredited: number;
  /** Remaining accepted contract value after this row (oldest → newest). */
  runningOutstanding: number;
  /** Remaining posted invoice balance after this row (oldest → newest). */
  runningDue: number;
  /**
   * @deprecated Prefer runningOutstanding. Kept as Outstanding alias for transitional callers.
   */
  runningBalance: number;
  invoiceStatus?: ClarityOneStatementInvoiceStatus | null;
  dueDate?: string | null;
  paymentMethod?: string | null;
  /** Optional invoice reference for payments (internal; not shown as jargon). */
  linkedInvoiceRef?: string | null;
  action: ClarityOneStatementAction;
};

export type ClarityOneStatementProposalCard = {
  proposalId: number;
  proposalRef: string;
  title: string;
  projectType: string | null;
  acceptedDate: string | null;
  proposalValue: number;
  /** Sum of posted invoice amounts for this proposal. */
  billed: number;
  /** Sum of payments received for this proposal. */
  paid: number;
  outstanding: number;
  due: number;
  lastActivityAt: string | null;
  transactions: ClarityOneStatementTransaction[];
};

export type ClarityOneStatementUnallocatedCredit = {
  id: string;
  date: string;
  reference: string;
  details: string;
  amount: number;
};

export type ClarityOneStatementOfAccount = {
  customerId: number;
  currency: typeof CLARITY_ONE_STATEMENT_CURRENCY;
  generatedAt: string;
  /** Singapore calendar day key used for Due Today diagnostics. */
  asOfDate: string;
  summary: {
    /** Sum of living Current Contract Values across accepted projects. */
    totalContractValue: number;
    /** Total payments received across the statement scope. */
    collected: number;
    /** Remaining accepted contract value after payments/credits. */
    outstanding: number;
    /** Posted invoice remainders (all unpaid/partial). */
    due: number;
    /** Posted invoice remainders due on asOfDate (Singapore). Kept for home diagnostics. */
    dueToday: number;
    /** Accepted project count contributing to totalContractValue. */
    projectCount: number;
  };
  /** Unified reverse-chronological commercial ledger (newest first). */
  activities: ClarityOneStatementTransaction[];
  /** Project options for filters (accepted proposals in scope). */
  projects: Array<{
    proposalId: number;
    proposalRef: string;
    title: string;
  }>;
  /** Per-proposal cards retained for PDF / reconciliation (same money authority). */
  proposals: ClarityOneStatementProposalCard[];
  /** Payments/credits not linked to an accepted proposal invoice. */
  unallocatedCredits: ClarityOneStatementUnallocatedCredit[];
  otherActivity: ClarityOneStatementProposalCard | null;
};

export function roundStatementMoney(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return roundSgd2(n);
}

export function clampNonNegativeMoney(value: unknown): number {
  return Math.max(0, roundStatementMoney(value));
}

/** Outstanding for one proposal (contract remaining after payments/credits). */
export function computeProposalOutstanding(input: {
  proposalValue: number;
  paymentsReceived: number;
  creditsApplied: number;
}): number {
  return clampNonNegativeMoney(
    roundStatementMoney(input.proposalValue) -
      roundStatementMoney(input.paymentsReceived) -
      roundStatementMoney(input.creditsApplied),
  );
}

/** Due for one proposal (posted invoice remainder). */
export function computeProposalDue(invoiceRemainders: number[]): number {
  return clampNonNegativeMoney(
    invoiceRemainders.reduce((sum, n) => sum + roundStatementMoney(n), 0),
  );
}

export function computeAccountSummary(proposals: Array<{ outstanding: number; due: number }>): {
  outstanding: number;
  due: number;
} {
  return {
    outstanding: clampNonNegativeMoney(
      proposals.reduce((s, p) => s + roundStatementMoney(p.outstanding), 0),
    ),
    due: clampNonNegativeMoney(proposals.reduce((s, p) => s + roundStatementMoney(p.due), 0)),
  };
}

/** Due Today: posted invoice remainders whose due date is the Singapore as-of day. */
export function resolveStatementDueDateKey(dueDate: string | null | undefined): string | null {
  if (!dueDate) return null;
  const raw = String(dueDate).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}$/.test(raw)) {
    const [y, m] = raw.split("-").map(Number);
    const endDay = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
    return `${y}-${String(m).padStart(2, "0")}-${String(endDay).padStart(2, "0")}`;
  }
  return getSingaporeDateKeyFromInstant(raw) ?? (raw.length >= 10 ? raw.slice(0, 10) : null);
}

export function computeStatementDueToday(
  invoices: Array<{ dueDate?: string | null; remaining: number }>,
  asOf: Date = new Date(),
): number {
  const today = singaporeTodayDateKey(asOf);
  return clampNonNegativeMoney(
    invoices.reduce((sum, inv) => {
      if (resolveStatementDueDateKey(inv.dueDate) !== today) return sum;
      return sum + roundStatementMoney(inv.remaining);
    }, 0),
  );
}

export function statementDateRangeStartKey(
  range: ClarityOneStatementDateRange | null | undefined,
  asOf: Date = new Date(),
): string | null {
  if (!range || range === "all") return null;
  const today = singaporeTodayDateKey(asOf);
  if (range === "this_year") return `${today.slice(0, 4)}-01-01`;
  const days = range === "last_30_days" ? 30 : 90;
  return addSingaporeCalendarDays(today, -(days - 1));
}

/**
 * Posted invoice Due may exceed contract Outstanding when source data is inconsistent.
 * Never rewrite invoice remainders to force Due ≤ Outstanding — only flag for ops/audit.
 */
export type StatementDueOutstandingInconsistency = {
  code: "due_exceeds_outstanding";
  scope: "proposal" | "account";
  proposalId?: number | null;
  proposalRef?: string | null;
  outstanding: number;
  due: number;
};

const DUE_VS_OUTSTANDING_EPSILON = 0.004;

export function statementDueExceedsOutstanding(outstanding: number, due: number): boolean {
  return (
    roundStatementMoney(due) > roundStatementMoney(outstanding) + DUE_VS_OUTSTANDING_EPSILON
  );
}

export function collectStatementDueOutstandingInconsistencies(
  proposals: Array<{
    proposalId: number;
    proposalRef?: string | null;
    outstanding: number;
    due: number;
  }>,
  account: { outstanding: number; due: number },
): StatementDueOutstandingInconsistency[] {
  const flags: StatementDueOutstandingInconsistency[] = [];
  for (const p of proposals) {
    if (statementDueExceedsOutstanding(p.outstanding, p.due)) {
      flags.push({
        code: "due_exceeds_outstanding",
        scope: "proposal",
        proposalId: p.proposalId,
        proposalRef: p.proposalRef ?? null,
        outstanding: roundStatementMoney(p.outstanding),
        due: roundStatementMoney(p.due),
      });
    }
  }
  if (statementDueExceedsOutstanding(account.outstanding, account.due)) {
    flags.push({
      code: "due_exceeds_outstanding",
      scope: "account",
      proposalId: null,
      outstanding: roundStatementMoney(account.outstanding),
      due: roundStatementMoney(account.due),
    });
  }
  return flags;
}

export function mapPortalInvoiceCustomerStatus(input: {
  status: string;
  remaining: number;
  dueDate?: string | null;
  asOf?: Date;
}): ClarityOneStatementInvoiceStatus {
  const status = String(input.status ?? "").toLowerCase();
  if (status === "voided" || status === "void") return "void";
  if (status === "paid" || roundStatementMoney(input.remaining) <= 0) return "paid";
  if (status === "partial" || (roundStatementMoney(input.remaining) > 0 && status === "raised")) {
    const overdue = isStatementDueDateOverdue(input.dueDate, input.asOf);
    if (overdue) return "overdue";
    if (status === "partial") return "partially_paid";
    return "unpaid";
  }
  if (status === "outstanding") return "overdue";
  if (isStatementDueDateOverdue(input.dueDate, input.asOf)) return "overdue";
  return "unpaid";
}

export function mapDealInvoiceCustomerStatus(input: {
  status: string;
  remaining: number;
  dueDate?: string | null;
  asOf?: Date;
}): ClarityOneStatementInvoiceStatus {
  const status = String(input.status ?? "").toLowerCase();
  if (status === "voided" || status === "void") return "void";
  if (status === "paid" || roundStatementMoney(input.remaining) <= 0) return "paid";
  if (status === "outstanding") return "overdue";
  if (isStatementDueDateOverdue(input.dueDate, input.asOf)) return "overdue";
  return "unpaid";
}

export function isStatementDueDateOverdue(
  dueDate: string | null | undefined,
  asOf: Date = new Date(),
): boolean {
  if (!dueDate) return false;
  const raw = String(dueDate).trim();
  if (!raw) return false;
  // scheduledDate may be YYYY-MM — treat end of month.
  let iso = raw;
  if (/^\d{4}-\d{2}$/.test(raw)) {
    const [y, m] = raw.split("-").map(Number);
    const end = new Date(Date.UTC(y!, m!, 0));
    iso = end.toISOString().slice(0, 10);
  } else if (raw.length > 10) {
    iso = raw.slice(0, 10);
  }
  const dueMs = Date.parse(`${iso}T23:59:59.999Z`);
  if (!Number.isFinite(dueMs)) return false;
  return asOf.getTime() > dueMs;
}

export function isPostedInvoiceStatus(status: string): boolean {
  const s = String(status ?? "").toLowerCase();
  return s !== "voided" && s !== "void" && s !== "draft" && s !== "cancelled" && s !== "canceled" && s !== "pending" && s !== "none";
}

/** Descriptive title fallback when proposalName is missing. */
export function resolveStatementProposalTitle(input: {
  proposalName?: string | null;
  proposalRef?: string | null;
  projectType?: string | null;
}): string {
  const name = String(input.proposalName ?? "").trim();
  if (name) return name;
  const type = String(input.projectType ?? "").trim();
  if (type) return type;
  const ref = String(input.proposalRef ?? "").trim();
  return ref || "Accepted proposal";
}

/** Same-day / equal-timestamp business order: approval → invoice → payment. */
export function statementTxnKindSortPriority(kind: ClarityOneStatementTxnKind): number {
  switch (kind) {
    case "acceptance":
      return 0;
    case "invoice":
      return 1;
    case "payment":
    case "credit_note":
    case "refund":
      return 2;
    default:
      return 9;
  }
}

export type ChronologicalTxnInput = Omit<
  ClarityOneStatementTransaction,
  | "runningOutstanding"
  | "runningDue"
  | "runningBalance"
  | "activityLabel"
  | "activityBadge"
  | "projectTitle"
  | "proposalId"
> & {
  activityLabel?: string;
  activityBadge?: ClarityOneStatementActivityBadge;
  projectTitle?: string | null;
  proposalId?: number | null;
  /**
   * When false, payments/credits do not reduce contract Outstanding
   * (unlinked / other-account invoices).
   */
  affectsContractOutstanding?: boolean;
};

export function compareStatementTransactionsChronological(
  a: Pick<ChronologicalTxnInput, "sortAt" | "kind" | "id">,
  b: Pick<ChronologicalTxnInput, "sortAt" | "kind" | "id">,
): number {
  const ta = Date.parse(a.sortAt) || 0;
  const tb = Date.parse(b.sortAt) || 0;
  if (ta !== tb) return ta - tb;
  const pa = statementTxnKindSortPriority(a.kind);
  const pb = statementTxnKindSortPriority(b.kind);
  if (pa !== pb) return pa - pb;
  return a.id.localeCompare(b.id);
}

function applyCommercialLedgerStep(
  state: { outstanding: number; due: number },
  row: ChronologicalTxnInput,
): { outstanding: number; due: number } {
  let outstanding = state.outstanding;
  let due = state.due;
  const affectsOutstanding = row.affectsContractOutstanding !== false;

  if (row.kind === "acceptance") {
    // Approval (or VO delta) establishes / increases contract Outstanding.
    if (affectsOutstanding) {
      outstanding = roundStatementMoney(outstanding + roundStatementMoney(row.billed));
    }
  } else if (row.kind === "invoice") {
    // Invoice moves part of the contract into Due — does not change Outstanding.
    due = roundStatementMoney(due + roundStatementMoney(row.billed));
  } else if (row.kind === "payment" || row.kind === "credit_note" || row.kind === "refund") {
    const paid = roundStatementMoney(row.paidCredited);
    due = clampNonNegativeMoney(due - paid);
    if (affectsOutstanding) {
      outstanding = clampNonNegativeMoney(outstanding - paid);
    }
  }

  return {
    outstanding: clampNonNegativeMoney(outstanding),
    due: clampNonNegativeMoney(due),
  };
}

function decorateTxn(
  row: ChronologicalTxnInput,
  runningOutstanding: number,
  runningDue: number,
): ClarityOneStatementTransaction {
  const { affectsContractOutstanding: _ignored, ...rest } = row;
  const activityBadge =
    rest.activityBadge ??
    (rest.kind === "acceptance"
      ? "APPROVED"
      : rest.kind === "payment"
        ? "PAYMENT"
        : rest.kind === "credit_note" || rest.kind === "refund"
          ? "CREDIT"
          : "INVOICE");
  return {
    ...rest,
    activityLabel: rest.activityLabel ?? rest.details,
    activityBadge,
    projectTitle: rest.projectTitle ?? null,
    proposalId: rest.proposalId ?? null,
    runningOutstanding,
    runningDue,
    runningBalance: runningOutstanding,
  };
}

/**
 * Compute running Outstanding + Due oldest→newest, then return newest-first for the live page.
 */
export function finalizeStatementTransactionsNewestFirst(
  rows: ChronologicalTxnInput[],
): ClarityOneStatementTransaction[] {
  return finalizeStatementTransactionsChronological(rows).reverse();
}

/** Compute running Outstanding + Due and return earliest-first (PDF / reconciliation). */
export function finalizeStatementTransactionsChronological(
  rows: ChronologicalTxnInput[],
): ClarityOneStatementTransaction[] {
  const chronological = [...rows].sort(compareStatementTransactionsChronological);

  let outstanding = 0;
  let due = 0;
  return chronological.map((row) => {
    const next = applyCommercialLedgerStep({ outstanding, due }, row);
    outstanding = next.outstanding;
    due = next.due;
    return decorateTxn(row, outstanding, due);
  });
}

/** Earliest-first view of an already-finalized newest-first activity list. */
export function statementActivitiesEarliestFirst(
  activities: ClarityOneStatementTransaction[],
): ClarityOneStatementTransaction[] {
  return [...activities].reverse();
}

export function formatStatementMoneySgd(amount: number): string {
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
  }).format(roundStatementMoney(amount));
}

export function formatStatementPaymentMethod(method: string | null | undefined): string {
  const m = String(method ?? "").trim().toLowerCase();
  if (!m) return "—";
  if (m === "paynow") return "PayNow";
  if (m === "card") return "Card";
  if (m === "bank_transfer") return "Bank transfer";
  if (m === "cash") return "Cash";
  return m.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatStatementInvoiceStatusLabel(
  status: ClarityOneStatementInvoiceStatus | null | undefined,
): string {
  switch (status) {
    case "unpaid":
      return "Unpaid";
    case "partially_paid":
      return "Partially paid";
    case "paid":
      return "Paid";
    case "overdue":
      return "Overdue";
    case "void":
      return "Void";
    default:
      return "—";
  }
}

/** Stable fingerprint used to keep PDF download consistent with the live page. */
export function statementConsistencyFingerprint(statement: ClarityOneStatementOfAccount): string {
  const payload = {
    customerId: statement.customerId,
    totalContractValue: statement.summary.totalContractValue,
    collected: statement.summary.collected,
    outstanding: statement.summary.outstanding,
    due: statement.summary.due,
    activityIds: statement.activities.map((t) => t.id),
    activityBalances: statement.activities.map((t) => ({
      id: t.id,
      billed: t.billed,
      paid: t.paidCredited,
      outstanding: t.runningOutstanding,
      due: t.runningDue,
    })),
    proposals: statement.proposals.map((p) => ({
      id: p.proposalId,
      value: p.proposalValue,
      billed: p.billed,
      paid: p.paid,
      outstanding: p.outstanding,
      due: p.due,
      txnIds: p.transactions.map((t) => t.id),
      txnBalances: p.transactions.map((t) => ({
        id: t.id,
        outstanding: t.runningOutstanding,
        due: t.runningDue,
      })),
    })),
    unallocated: statement.unallocatedCredits.map((c) => c.id),
    otherTxnIds: statement.otherActivity?.transactions.map((t) => t.id) ?? [],
    other: statement.otherActivity
      ? {
          billed: statement.otherActivity.billed,
          paid: statement.otherActivity.paid,
          outstanding: statement.otherActivity.outstanding,
          due: statement.otherActivity.due,
        }
      : null,
  };
  return JSON.stringify(payload);
}
